import { Button, Col, Input, Layout, message, Row, Upload, Form, Select } from "antd";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import PlainTable from "../../Components/PlainTable";
import { Equal, X, Inbox, ExternalLink, FileText } from "lucide-react";
import { useEffect, useState } from "react";
import {
  formatNumbers,
  formatString,
  handleKeyPress,
  handleOnWheel,
  navigateTo,
} from "../../Utils/Common";
import { useLocation, useParams } from "react-router-dom";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import ConfirmAction from "../../Components/ConfirmAction/ConfirmAction";
import ReasonModal from "../../Components/CustomModals/ReasonModal";
import ViewAttachmentModal from "../../Components/CustomModals/ViewAttachmentModal";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";

interface EndOfDayFormProps {
  add?: boolean;
  view?: boolean;
  edit?: boolean;
  check?: boolean;
  approval?: boolean;
}

const { Dragger } = Upload;

const initialBillData = [
  { bill: "1,000",   amount: 1000,  key: "peso_1000", count: 0, total: 0 },
  { bill: "500",     amount: 500,   key: "peso_500",  count: 0, total: 0 },
  { bill: "200",     amount: 200,   key: "peso_200",  count: 0, total: 0 },
  { bill: "100",     amount: 100,   key: "peso_100",  count: 0, total: 0 },
  { bill: "50",      amount: 50,    key: "peso_50",   count: 0, total: 0 },
  { bill: "20",      amount: 20,    key: "peso_20",   count: 0, total: 0 },
  { bill: "10 coin", amount: 10,    key: "peso_10",   count: 0, total: 0 },
  { bill: "5 coin",  amount: 5,     key: "peso_5",    count: 0, total: 0 },
  { bill: "1 coin",  amount: 1,     key: "peso_1",    count: 0, total: 0 },
  { bill: "25 cent", amount: 0.25,  key: "cent_25",   count: 0, total: 0 },
  { bill: "10 cent", amount: 0.1,   key: "cent_10",   count: 0, total: 0 },
  { bill: "5 cent",  amount: 0.05,  key: "cent_5",    count: 0, total: 0 },
  { bill: "1 cent",  amount: 0.01,  key: "cent_1",    count: 0, total: 0 },
];

const EndOfDayForm: React.FC<EndOfDayFormProps> = ({ add, view, edit, check, approval }) => {
  const action = add ? "add" : view ? "view" : edit ? "edit" : check ? "check" : approval ? "approval" : "add";

  const location = useLocation();
  const { id: eod_id } = useParams<{ id: string }>();

  const [loading,        setLoading]        = useState("");
  const [remarks,        setRemarks]        = useState("");
  const [checkRemarks,   setCheckRemarks]   = useState("");
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);   // ← multiple URLs
  const [attachments,    setAttachments]    = useState<File[]>([]);      // ← multiple files
  const [branchID,       setBranchID]       = useState<string | undefined>(undefined);
  const [branchName,     setBranchName]     = useState("");
  const [branches,       setBranches]       = useState<any[]>([]);
  const [showAttachment, setShowAttachment] = useState<string | null>(null); // ← holds the URL to preview

  const [cashSalesData,  setCashSalesData]  = useState(initialBillData.map((i) => ({ ...i })));
  const [cashSalesTotal, setCashSalesTotal] = useState(0);

  const [systemCashSales,         setSystemCashSales]         = useState(0);
  const [systemAgentDisbursement, setSystemAgentDisbursement] = useState(0);

  const cashSalesVariance = cashSalesTotal - systemCashSales;

  const isReadOnly  = action === "view" || action === "check" || action === "approval";
  const isEditOrAdd = action === "add"  || action === "edit";
  const isReviewing = action === "check" || action === "approval";

  const pageTitle = (() => {
    switch (action) {
      case "view":     return "View End of Day";
      case "edit":     return "Edit End of Day";
      case "check":    return "Check End of Day";
      case "approval": return "Approve End of Day";
      default:         return "Add End of Day";
    }
  })();

  const getColumns = (
    data: typeof initialBillData,
    handleChange: (e: any, index: number, amount: number) => void
  ) => [
    {
      title: "Bill",
      dataIndex: "bill",
      align: "text-start",
      width: 200,
      render: (text: any) => (
        <span className="text-textColor font-bold">{text}</span>
      ),
    },
    {
      title: "operand",
      dataIndex: "operand",
      width: 50,
      render: () => <X size={15} />,
    },
    {
      title: "Count",
      dataIndex: "count",
      align: "text-start",
      width: 200,
      render: (text: any, record: any, index: number) => (
        <Input
          type="number"
          className="w-full my-1"
          placeholder="0"
          value={text}
          onWheel={(e) => handleOnWheel(e)}
          onKeyDown={(e) => handleKeyPress(e, "-")}
          min={0}
          disabled={isReadOnly}
          onChange={(e) => handleChange(e, index, record.amount)}
        />
      ),
    },
    {
      title: "equal",
      dataIndex: "equal",
      width: 50,
      render: () => <Equal size={15} className="ms-10" />,
    },
    {
      title: "Total",
      dataIndex: "total",
      align: "text-end",
      width: 200,
      render: (text: any) => (
        <span className="text-textColor font-bold">{formatNumbers(text)}</span>
      ),
    },
  ];

  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) message.error(`${file.name} exceeds the 2MB size limit.`);
    return false; // always prevent auto-upload
  };

  const handleUploadChange = (info: any) => {
    const validFiles: File[] = info.fileList
      .filter((f: any) => f.size / 1024 / 1024 < 2)
      .map((f: any) => f.originFileObj)
      .filter(Boolean);
    setAttachments(validFiles);
  };

  async function fetchBranches() {
    setBranches([]);
    setLoading("fetch");
    const response = await getRequest(`branches/get`, {}, true);
    if (response.success) setBranches(response.data.data);
    setLoading("");
  }

  function handleCashSalesChange(e: any, index: number, amount: number) {
    const count   = e.target.value;
    const updated = [...cashSalesData];
    updated[index] = { ...updated[index], count, total: amount * count };
    setCashSalesData(updated);
    setCashSalesTotal(updated.reduce((acc, item) => acc + item.total, 0));
  }

  function buildDetailPayload(data: typeof initialBillData) {
    return data.reduce((acc: any, item) => {
      acc[item.key] = item.count ?? 0;
      return acc;
    }, {});
  }

  function mapDetailToData(base: typeof initialBillData, result: any): typeof initialBillData {
    return base.map((item) => {
      const count = parseFloat(result[item.key] ?? 0);
      return { ...item, count, total: item.amount * count };
    });
  }

  async function fetchData() {
    setLoading("fetch");

    const response = await getRequest(`eods/get`, { eod_id }, true);
    if (!response.success) {
      message.error("Failed to fetch EOD record.");
      setLoading("");
      return;
    }

    const data = response.data.data;
    const eod  = data[0];

    setRemarks(eod.remarks ?? "");
    setBranchID(eod.branch_id ?? undefined);
    setBranchName(eod.branch_name ?? "");
    setSystemCashSales(parseFloat(eod.system_cash_sales ?? 0));
    setSystemAgentDisbursement(parseFloat(eod.system_agent_disbursement ?? 0));

    // ── Support both single string and array from backend ──
    if (Array.isArray(eod.attachments)) {
      setAttachmentUrls(eod.attachments);
    } else if (eod.attachment) {
      setAttachmentUrls([eod.attachment]);
    } else {
      setAttachmentUrls([]);
    }

    const cashDetail = data.find((d: any) => d.type === "cash_sale");
    if (cashDetail) {
      const updated = mapDetailToData(initialBillData.map((i) => ({ ...i })), cashDetail);
      setCashSalesData(updated);
      setCashSalesTotal(updated.reduce((acc, item) => acc + item.total, 0));
    }

    if (action === "edit") {
      const branchResponse = await getRequest(`branches/get`, {}, true);
      if (branchResponse.success) setBranches(branchResponse.data.data);
    }

    setLoading("");
  }

  function buildFormData(extra: Record<string, string> = {}) {
    const formData = new FormData();
    formData.append("remarks",             remarks);
    formData.append("actual_cash_sales",   String(cashSalesTotal));
    formData.append("cash_sales_variance", String(cashSalesVariance));
    formData.append("cash_sale_detail",    JSON.stringify(buildDetailPayload(cashSalesData)));
    formData.append("branch_id",           branchID ?? "");
    Object.entries(extra).forEach(([k, v]) => formData.append(k, v));
    // ── Append each file under attachment[] ──
    attachments.forEach((file) => formData.append("attachment[]", file));
    return formData;
  }

  async function saveCashCount() {
    if (cashSalesTotal === 0) { message.warning("Total cannot be zero."); return; }
    setLoading("submit");
    const response = await postRequest(`eods/create`, buildFormData(), true);
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { setLoading(""); navigateTo("/end-of-day"); }, 1000);
    } else {
      setLoading("");
      message.error(response?.data?.response?.data?.messages?.error_messages?.[0] ?? "Failed to save End of Day.");
    }
  }

  async function updateCashCount() {
    if (cashSalesTotal === 0) { message.warning("Total cannot be zero."); return; }
    setLoading("submit");
    const response = await postRequest(`eods/update`, buildFormData({ eod_id: String(eod_id) }), true);
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { setLoading(""); navigateTo("/end-of-day"); }, 1000);
    } else {
      setLoading("");
      message.error(response?.data?.response?.data?.messages?.error_messages?.[0] ?? "Failed to update End of Day.");
    }
  }

  async function checkEOD() {
    setLoading("check");
    const response = await postRequest(
      `eods/change_status`,
      { eod_id, new_status: "for_approval", check_remarks: checkRemarks },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { setLoading(""); navigateTo("/end-of-day"); }, 1000);
    } else {
      setLoading("");
      message.error(response?.data?.response?.data?.messages?.error_messages?.[0] ?? "Failed to check End of Day.");
    }
  }

  async function approveEOD() {
    setLoading("approve");
    const response = await postRequest(
      `eods/change_status`,
      { eod_id, new_status: "approved", check_remarks: checkRemarks },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { setLoading(""); navigateTo("/end-of-day"); }, 1000);
    } else {
      setLoading("");
      message.error(response?.data?.response?.data?.messages?.error_messages?.[0] ?? "Failed to approve End of Day.");
    }
  }

  async function rejectEOD() {
    if (!checkRemarks.trim()) {
      message.warning("Please provide remarks before disapproving.");
      return;
    }
    setShowDisapproveModal(false);
    setLoading("reject");
    const response = await postRequest(
      `eods/change_status`,
      { eod_id, new_status: "disapproved", check_remarks: checkRemarks },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { setLoading(""); navigateTo("/end-of-day"); }, 1000);
    } else {
      setLoading("");
      message.error(response?.data?.response?.data?.messages?.error_messages?.[0] ?? "Failed to reject End of Day.");
    }
  }

  useEffect(() => {
    if (action === "add") {
      fetchBranches();
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const VarianceRow = ({ label, actual, system, variance }: {
    label: string; actual: number; system: number; variance: number;
  }) => (
    <div className="grid grid-cols-4 gap-4 py-2 border-b border-gray-200 text-sm">
      <span className="font-semibold col-span-1">{label}</span>
      <span className="text-end">{formatNumbers(actual)}</span>
      <span className="text-end">{formatNumbers(system)}</span>
      <span className={`text-end font-bold ${variance < 0 ? "text-red-500" : variance > 0 ? "text-green-600" : "text-textColor"}`}>
        {formatNumbers(variance)}
      </span>
    </div>
  );

  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader title={pageTitle} withDateRangeFilter={false} withAddButton={false} withSearchBtn={false} />
          </Col>

          {loading !== "fetch" && (
            <Col span={24} className="text-textColor">
              <div className="form-container">

                {/* ── Branch ───────────────────────────────── */}
                <div className="mt-5 pt-3 pb-2 flex flex-col gap-2">
                  <span className="font-bold">Branch</span>
                  {isReadOnly ? (
                    <span className="text-textColor font-medium">{branchName || "—"}</span>
                  ) : (
                    <Select
                      placeholder="Select branch"
                      showSearch
                      allowClear
                      value={branchID}
                      onChange={(value) => setBranchID(value)}
                      options={branches.map((b) => ({ value: b.id, label: b.name }))}
                      filterOption={(input, option) =>
                        option?.label.toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  )}
                </div>

                {/* ── Cash Sales ───────────────────────────── */}
                <Row className="w-full mb-2 mt-4">
                  <Col><h2><strong>Total Cash Sales Collected</strong></h2></Col>
                </Row>
                <PlainTable
                  headers={getColumns(cashSalesData, handleCashSalesChange)}
                  rows={cashSalesData}
                  withHeader={false}
                  withBorder={false}
                />
                <div className="border-t border-t-textColor mt-5 pt-3 pb-2 flex justify-between">
                  <span className="font-bold text-xl">TOTAL (Cash Sales)</span>
                  <span className="font-bold text-xl">{formatNumbers(cashSalesTotal)}</span>
                </div>

                {/* ── Variance Summary ─────────────────────── */}
                <div className="mt-8">
                  <h2 className="mb-3"><strong>Variance Summary</strong></h2>
                  <div className="grid grid-cols-4 gap-4 py-2 border-b border-gray-400 text-sm font-bold">
                    <span>Type</span>
                    <span className="text-end">Actual</span>
                    <span className="text-end">System</span>
                    <span className="text-end">Variance</span>
                  </div>
                  <VarianceRow label="Cash Sales" actual={cashSalesTotal} system={systemCashSales} variance={cashSalesVariance} />
                </div>

                {/* ── Remarks ──────────────────────────────── */}
                <div className="mt-5 pt-3 pb-2 flex flex-col gap-2">
                  <span className="font-bold">REMARKS</span>
                  <Input.TextArea value={remarks} disabled={isReadOnly} rows={3} onChange={(e) => setRemarks(e.target.value)} />
                </div>

                {/* ── Upload (add / edit) ───────────────────── */}
                {isEditOrAdd && (
                  <Col xs={24} className="pt-1">
                    <span className="edit-label mb-2">Deposit Slip(s):</span>
                    <Form.Item
                      name="attachment"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => e?.fileList || []}
                    >
                      <Dragger
                        name="file"
                        multiple
                        beforeUpload={handleBeforeUpload}
                        onChange={handleUploadChange}
                        accept=".pdf,image/*"
                      >
                        <p className="ant-upload-drag-icon flex justify-center items-center text-3xl"><Inbox /></p>
                        <p className="ant-upload-text">Click or drag files to upload</p>
                        <p className="ant-upload-hint">Supports PDF, Image formats. You can upload multiple files.</p>
                      </Dragger>
                    </Form.Item>
                    <p className="text-textColor pt-2">Note: Maximum file size is 2MB per file.</p>
                  </Col>
                )}

                {/* ── View attachments (read-only) ──────────── */}
                {isReadOnly && attachmentUrls.length > 0 && (
                  <Col xs={24} className="pt-1 mt-4">
                    <span className="font-bold block mb-2">EOD Attachment(s)</span>
                    <div className="flex flex-wrap gap-2">
                      {attachmentUrls.map((url, index) => (
                        <Button
                          key={index}
                          icon={<ExternalLink size={14} />}
                          onClick={() => setShowAttachment(url)}
                        >
                          Attachment {index + 1}
                        </Button>
                      ))}
                    </div>
                  </Col>
                )}

                {/* ── Actions ──────────────────────────────── */}
                <Col span={24}>
                  <Row className="flex justify-end px-10 my-5 pb-[50px] sm:pb-0" gutter={[10, 10]}>

                    <Col xs={24} md={12} lg={3} onClick={() => navigateTo("/end-of-day")}>
                      <Button className="w-full">{isReadOnly ? "Close" : "Cancel"}</Button>
                    </Col>

                    {action === "add" && (
                      <Col xs={24} md={12} lg={3}>
                        <ConfirmAction
                          title="Are you sure you want to save this End of Day record?"
                          onConfirm={saveCashCount}
                          okText="Confirm"
                          cancelText="Cancel"
                          okButtonProps={{ loading: loading === "submit" }}
                        >
                          <Button
                            className="w-full"
                            type="primary"
                            disabled={!branchID}
                            loading={loading === "submit"}
                            onClick={(e) => {
                              if (cashSalesTotal === 0) {
                                e.preventDefault();
                                e.stopPropagation();
                                message.warning("Total cannot be zero.");
                              }
                            }}
                          >
                            Save
                          </Button>
                        </ConfirmAction>
                      </Col>
                    )}

                    {action === "edit" && (
                      <Col xs={24} md={12} lg={3}>
                        <ConfirmAction
                          title="Are you sure you want to update this End of Day record?"
                          onConfirm={updateCashCount}
                          okText="Confirm"
                          cancelText="Cancel"
                          okButtonProps={{ loading: loading === "submit" }}
                        >
                          <Button
                            className="w-full"
                            type="primary"
                            disabled={!branchID}
                            loading={loading === "submit"}
                            onClick={(e) => {
                              if (cashSalesTotal === 0) {
                                e.preventDefault();
                                e.stopPropagation();
                                message.warning("Total cannot be zero.");
                              }
                            }}
                          >
                            Update
                          </Button>
                        </ConfirmAction>
                      </Col>
                    )}

                    {action === "check" && (
                      <Col xs={24} md={12} lg={3}>
                        <ConfirmAction
                          title="Are you sure you want to mark this record as checked?"
                          onConfirm={checkEOD}
                          okText="Confirm"
                          cancelText="Cancel"
                          okButtonProps={{ loading: loading === "check" }}
                        >
                          <Button
                            className="w-full"
                            type="primary"
                            disabled={!branchID}
                            loading={loading === "check"}
                          >
                            Check
                          </Button>
                        </ConfirmAction>
                      </Col>
                    )}

                    {action === "approval" && (
                      <>
                        <Col xs={24} md={12} lg={3}>
                          <Button
                            className="w-full"
                            danger
                            disabled={!branchID}
                            loading={loading === "reject"}
                            onClick={() => {
                              setCheckRemarks("");
                              setShowDisapproveModal(true);
                            }}
                          >
                            Disapprove
                          </Button>
                        </Col>
                        <Col xs={24} md={12} lg={3}>
                          <ConfirmAction
                            title="Are you sure you want to approve this End of Day record?"
                            onConfirm={approveEOD}
                            okText="Approve"
                            cancelText="Cancel"
                            okButtonProps={{ loading: loading === "approve" }}
                          >
                            <Button
                              className="w-full"
                              type="primary"
                              disabled={!branchID}
                              loading={loading === "approve"}
                            >
                              Approve
                            </Button>
                          </ConfirmAction>
                        </Col>
                      </>
                    )}

                  </Row>
                </Col>
              </div>
            </Col>
          )}
        </Row>
      </Layout>

      <ViewAttachmentModal
        showModal={!!showAttachment}
        attachmentUrl={showAttachment ?? ""}
        onCancel={() => setShowAttachment(null)}
      />

      <ReasonModal
        showModal={showDisapproveModal}
        onCancel={() => setShowDisapproveModal(false)}
        title="Please provide a reason for disapproval:"
        reasonValue={checkRemarks}
        onReasonChange={(e: any) => setCheckRemarks(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowDisapproveModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading === "reject"}
            disabled={!checkRemarks.trim()}
            onClick={rejectEOD}
          >
            Confirm Disapprove
          </Button>,
        ]}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default EndOfDayForm;